import { graphql, useStaticQuery } from 'gatsby';
import { SiteMetadataQuery } from 'graphql-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { findTranslation } from '../../utils/findTranslation';
import { usePageContext } from '../context/pageContext';

type MetaProps = JSX.IntrinsicElements['meta'];

interface Props {
  rawMeta?: any;
  title?: string;
  fallbackTitle?: string;
  description?: string;
  image?: string;
  fallbackImage?: string;
  meta?: MetaProps[];
}

export const SEO: React.FC<Props> = ({
  rawMeta,
  meta = [],
  title,
  image,
  description,
  fallbackImage,
  fallbackTitle,
}) => {
  const { site, sanitySiteMetadata } = useStaticQuery<SiteMetadataQuery>(QUERY);
  const { lang, originalPath, slugs, defaultLang } = usePageContext();
  const defaultLanguage = site?.siteMetadata?.defaultLanguage as string;
  const extraLanguages = site?.siteMetadata?.extraLanguages as string[];

  const host = sanitySiteMetadata?.domainName;

  const websiteTitle = findTranslation(sanitySiteMetadata?._rawMetaTitle, lang, defaultLang);

  const metaTitle =
    findTranslation(rawMeta?.metaTitle, lang, defaultLang) || fallbackTitle || websiteTitle;

  const metaDescription =
    findTranslation(rawMeta?.metaDescription, lang, defaultLang) ||
    findTranslation(sanitySiteMetadata?._rawMetaDescription, lang, defaultLang);
  ('');

  const ogTitle =
    title ||
    findTranslation(rawMeta?.ogTitle, lang, defaultLang) ||
    findTranslation(sanitySiteMetadata?._rawOgTitle, lang, defaultLang) ||
    fallbackTitle ||
    metaTitle ||
    '';

  const ogDescription =
    description ||
    findTranslation(rawMeta?.ogDescription, lang, defaultLang) ||
    findTranslation(sanitySiteMetadata?._rawOgDescription, lang, defaultLang) ||
    metaDescription ||
    '';

  const ogImage =
    image ||
    rawMeta?.ogImage?.asset?.url ||
    fallbackImage ||
    sanitySiteMetadata?.ogImage?.asset?.url ||
    '';

  const twitterTitle =
    title ||
    findTranslation(rawMeta?.twitterTitle, lang, defaultLang) ||
    findTranslation(sanitySiteMetadata?._rawTwitterTitle, lang, defaultLang) ||
    fallbackTitle ||
    metaTitle ||
    '';

  const twitterDescription =
    description ||
    findTranslation(rawMeta?.twitterDescription, lang, defaultLang) ||
    findTranslation(sanitySiteMetadata?._rawTwitterDescription, lang, defaultLang) ||
    metaDescription ||
    '';

  const twitterImage =
    image ||
    rawMeta?.twitterImage?.asset?.url ||
    fallbackImage ||
    sanitySiteMetadata?.twitterImage?.asset?.url ||
    '';

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={metaTitle}
      titleTemplate={metaTitle === websiteTitle ? `%s` : `%s | ${websiteTitle || ''}`}
      meta={[
        { name: `theme-color`, content: `#0d0e0d` },
        { name: `apple-mobile-web-app-capable`, content: `yes` },
        {
          name: `apple-mobile-web-app-status-bar-style`,
          content: `black-translucent`,
        },
        {
          name: `description`,
          content: metaDescription,
        },
        {
          /* This is the title of your webpage. Remember that this will be shown whenever someone links your website, so make it quick, snappy and able to draw attention */
          property: `og:title`,
          content: ogTitle,
        },
        {
          property: `og:description`,
          content: ogDescription,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          property: `og:image`,
          content: ogImage,
        },
        {
          name: `twitter:card`,
          content: `summary`,
        },
        {
          name: `twitter:title`,
          content: twitterTitle,
        },
        {
          name: `twitter:description`,
          content: twitterDescription,
        },
        {
          name: `twitter:image`,
          content: twitterImage,
        },
        // @ts-ignore
      ].concat(meta)}
      link={[
        {
          rel: 'canonical',
          hrefLang: lang,
          href:
            // lang === defaultLanguage
            //   ? `${host}${originalPath}`
            //   : `${host}/${lang}${slugs?.[lang] ?? slugs?.[defaultLang] ?? ''}`,
            lang === defaultLanguage
              ? `${host}${originalPath}${
                  originalPath?.endsWith('/') || (originalPath?.length ?? 0) === 0 ? '' : '/'
                }`
              : `${host}/${lang}${slugs?.[lang] ?? slugs?.[defaultLang] ?? ''}${
                  (slugs?.[lang] ?? slugs?.[defaultLang] ?? '')?.endsWith('/') ? '' : '/'
                }`,
        },
        {
          rel: 'alternate',
          hrefLang: 'x-default',
          href: `${host}${originalPath}${
            originalPath?.endsWith('/') || (originalPath?.length ?? 0) === 0 ? '' : '/'
          }`,
        },
        {
          rel: 'alternate',
          hrefLang: defaultLang,
          href: `${host}${originalPath}${
            originalPath?.endsWith('/') || (originalPath?.length ?? 0) === 0 ? '' : '/'
          }`,
        },

        ...extraLanguages.map((extraLang: string) => ({
          rel: 'alternate',
          hrefLang: extraLang,
          href: `${host}/${extraLang}${slugs?.[extraLang] ?? slugs?.[defaultLang] ?? ''}${
            (slugs?.[lang] ?? slugs?.[defaultLang])?.endsWith('/') ||
            (slugs?.[lang] ?? slugs?.[defaultLang]?.length ?? 0) === 0
              ? ''
              : '/'
          }`,
        })),
      ]}
    />
  );
};

const QUERY = graphql`
  query SiteMetadata {
    site {
      siteMetadata {
        defaultLanguage
        extraLanguages
      }
    }
    sanitySiteMetadata {
      _rawMetaDescription(resolveReferences: { maxDepth: 10 })
      _rawMetaTitle(resolveReferences: { maxDepth: 10 })
      _rawTwitterDescription(resolveReferences: { maxDepth: 10 })
      _rawTwitterTitle(resolveReferences: { maxDepth: 10 })
      _rawOgDescription(resolveReferences: { maxDepth: 10 })
      _rawOgTitle(resolveReferences: { maxDepth: 10 })
      ogImage {
        asset {
          url
        }
      }
      twitterImage {
        asset {
          url
        }
      }
      domainName
      domainNameUs
    }
  }
`;
